import type { ReactNode } from 'react';
import { useViewer } from 'hooks/useViewer';
import { useMyRequestedAvailabilityIds } from 'screens/Find/components/RequestToPlay';
import type { FindPlayer } from 'screens/Find/types';
import { Button } from 'components/Button';
import DetailModalShell from 'components/Find/DetailModalShell';
import { NeighborhoodList } from 'components/Find/Neighborhoods';
import {
  PlayerAvailability,
  PlayerHeader,
  PlayerLookingFor,
  PlayerPreferredLocations,
} from 'components/Find/PlayerDetails';
import { DetailSection, PersonCard } from 'components/Find/detail';

interface Props {
  player: FindPlayer | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestToPlay?: (playerId: string) => void;
  /** Hide the request footer — e.g. the viewer already has a pending request. */
  hideRequestFooter?: boolean;
  /** Rendered after the sections — e.g. the viewer's own request to this availability. */
  children?: ReactNode;
}

/** Detail view for a player card on the Find "Players" tab. */
export default function PlayerDetailsModal({
  player,
  isOpen,
  onClose,
  onRequestToPlay,
  hideRequestFooter = false,
  children,
}: Props) {
  const { userId } = useViewer();
  // The owner of an availability can't request themselves — hide the footer.
  const isOwnAvailability = Boolean(
    player?.targetUserId && userId && player.targetUserId === userId,
  );

  const requestedAvailabilityIds = useMyRequestedAvailabilityIds();
  const canShowFooter = !isOwnAvailability && !hideRequestFooter;
  const hasPendingRequest = Boolean(player && requestedAvailabilityIds.has(player.id));

  return (
    <DetailModalShell
      isOpen={isOpen}
      onClose={onClose}
      onRequestToPlay={() => player && onRequestToPlay?.(player.id)}
      hideRequestFooter={!canShowFooter}
      sizeDesktop="lg"
      footer={
        canShowFooter && hasPendingRequest ? (
          <Button variant="brand" size="lg" disabled>
            Request pending
          </Button>
        ) : undefined
      }
    >
      {player && (
        <>
          <div className="flex flex-col gap-ds-xl">
            <PlayerHeader player={player} sizeDesktop="lg" />
            <PlayerLookingFor player={player} sizeDesktop="lg" />
          </div>

          <div className="my-ds-xl h-px w-full bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />

          <div className="flex flex-col gap-ds-3xl">
            {player.username && (
              <DetailSection title="Player" sizeDesktop="lg">
                <PersonCard
                  name={player.name}
                  username={player.username}
                  imageUrl={player.imageUrl}
                  verificationLevel={player.verificationLevel}
                  sizeDesktop="lg"
                />
              </DetailSection>
            )}

            {player.availability.length > 0 && (
              <PlayerAvailability availability={player.availability} sizeDesktop="lg" />
            )}

            {player.neighborhoods && player.neighborhoods.length > 0 && (
              <DetailSection
                title={`Preferred neighborhoods: ${player.neighborhoods.length}`}
                sizeDesktop="lg"
              >
                <NeighborhoodList names={player.neighborhoods.map((n) => n.name)} />
              </DetailSection>
            )}

            {player.preferredLocations && player.preferredLocations.length > 0 && (
              <PlayerPreferredLocations locations={player.preferredLocations} sizeDesktop="lg" />
            )}
          </div>

          {children && <div className="mt-ds-xl">{children}</div>}
        </>
      )}
    </DetailModalShell>
  );
}
